import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function formatArticleError(err, t) {
  const msg = err?.message || '';
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(msg) || /exceeded/i.test(msg)) {
    return t('content:articleErrorTimeout');
  }
  const detail = err?.response?.data?.detail;
  if (detail) {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
        .join(' ');
    }
  }
  return msg || t('content:articleErrorGeneric');
}

/** Normalize user input: full IRI, internal id, or site path segment for the API. */
function normalizeKgNodeInput(raw) {
  const s = String(raw).trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (/^\d+$/.test(s)) return s;
  if (s.includes('/') && !s.startsWith('http')) {
    return s.startsWith('/') ? s : `/${s}`;
  }
  return s;
}

export default function KGToText({ apiUrl, focusNodeId = null, onOpenNodeInGraph }) {
  const { t } = useTranslation();
  const [nodeId, setNodeId] = useState('');
  const [sparqlQuery, setSparqlQuery] = useState('');
  const [language, setLanguage] = useState('es');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!isLoading) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    setElapsedSec(0);
    const started = Date.now();
    tickRef.current = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [isLoading]);

  useEffect(() => {
    if (focusNodeId == null || String(focusNodeId).trim() === '') return;
    setNodeId(String(focusNodeId).trim());
  }, [focusNodeId]);

  const generate = async () => {
    if (!nodeId.trim() && !sparqlQuery.trim()) return;
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const normalized = normalizeKgNodeInput(nodeId);
      const data = await apiUrl.kgToText(
        normalized || null,
        sparqlQuery.trim() || null,
        language
      );
      setResult(data);
    } catch (err) {
      setError(formatArticleError(err, t));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <p className="text-label text-ink-muted">{t('content:articlesSectionIntro')}</p>
      <div>
        <label className="text-label text-ink-muted uppercase tracking-widest block mb-1">
          {t('content:articleNodeId')}
        </label>
        <input
          type="text"
          value={nodeId}
          onChange={(e) => setNodeId(e.target.value)}
          placeholder={t('content:nodeIdPlaceholder')}
          className="w-full bg-surface-raised border border-border-subtle rounded p-3 text-sm text-ink-secondary placeholder-ink-muted focus:outline-none focus:border-border-strong"
          aria-label={t('content:articleNodeId')}
        />
        <p className="text-xs text-ink-muted mt-2 max-w-prose leading-relaxed">{t('content:articleNodeIdHelp')}</p>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-label text-ink-muted uppercase tracking-widest">
          {t('content:articleLanguage')}
        </span>
        {['es', 'ca', 'en'].map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`text-label font-medium tracking-widest transition-colors ${
              language === lang ? 'text-accent-text' : 'text-ink-muted hover:text-ink-secondary'
            }`}
            aria-pressed={language === lang}
          >
            {lang.toUpperCase()}
          </button>
        ))}
      </div>

      <button
        onClick={generate}
        disabled={isLoading || (!nodeId.trim() && !sparqlQuery.trim())}
        className="text-sm text-accent-text hover:text-accent-hover disabled:text-ink-muted transition-colors font-medium"
        aria-busy={isLoading}
        aria-label={t('content:generateArticle')}
      >
        {isLoading ? t('content:generating') : t('content:generateArticle')}
      </button>

      {isLoading && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="rounded border border-accent/40 bg-surface-raised/80 p-4 space-y-2"
        >
          <p className="text-sm text-ink-secondary animate-pulse">
            {t('content:articleGeneratingProgress', { seconds: elapsedSec })}
          </p>
          <p className="text-xs text-ink-muted leading-relaxed">{t('content:articleGeneratingWaitHint')}</p>
        </div>
      )}

      {error && (
        <p className="text-sm text-signal-error pl-4 border-l-2 border-signal-error">{error}</p>
      )}

      {result && (
        <div className="pl-4 border-l-2 border-accent space-y-3">
          <h2 className="text-base font-semibold text-ink">{result.title}</h2>
          <ArticleBody markdown={result.article || ''} />
          {result.audit_pack && <AuditPack pack={result.audit_pack} />}
          {result.sources && result.sources.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-border-subtle">
              <p className="text-label text-ink-muted uppercase tracking-widest">
                {t('content:articleSourcesLabel')}
              </p>
              <p className="text-xs text-ink-muted leading-relaxed">{t('content:articleSourcesIdExplanation')}</p>
              <ul className="space-y-1.5">
                {result.sources.map((src, i) => (
                  <li key={i}>
                    {typeof onOpenNodeInGraph === 'function' ? (
                      <button
                        type="button"
                        onClick={() => onOpenNodeInGraph(src)}
                        className="text-left w-full text-sm text-accent-text hover:text-accent-hover hover:underline break-all rounded px-1 -mx-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-accent/50"
                        aria-label={`${t('content:articleSourceOpenInGraph')}: ${src}`}
                      >
                        {/^\d+$/.test(String(src).trim())
                          ? `${t('content:graphNodeInternalId')}: ${String(src).trim()}`
                          : String(src)}
                      </button>
                    ) : (
                      <p className="text-label text-ink-muted break-all">
                        {/^\d+$/.test(String(src).trim())
                          ? `${t('content:graphNodeInternalId')}: ${String(src).trim()}`
                          : String(src)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-label text-ink-muted">{result.execution_time}s</p>
        </div>
      )}
    </div>
  );
}

// Minimal markdown renderer — enough for the audit-explanation prompt output
// (H2 headings, fenced code blocks, inline code, bold). Intentionally small
// to avoid adding a dependency for a narrow use case.
function ArticleBody({ markdown }) {
  if (!markdown) return null;

  const lines = String(markdown).split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      i++;
      const code = [];
      while (i < lines.length && !lines[i].startsWith('```')) {
        code.push(lines[i]); i++;
      }
      i++;
      blocks.push({ kind: 'code', lang, body: code.join('\n') });
      continue;
    }

    if (/^##\s+/.test(line)) {
      blocks.push({ kind: 'h2', body: line.replace(/^##\s+/, '') });
      i++;
      continue;
    }
    if (/^###\s+/.test(line)) {
      blocks.push({ kind: 'h3', body: line.replace(/^###\s+/, '') });
      i++;
      continue;
    }

    // Collect a paragraph until blank line.
    const para = [];
    while (i < lines.length && lines[i].trim() !== ''
           && !lines[i].startsWith('```') && !/^#{2,3}\s+/.test(lines[i])) {
      para.push(lines[i]); i++;
    }
    if (para.length) blocks.push({ kind: 'p', body: para.join('\n') });
    // consume blank line(s)
    while (i < lines.length && lines[i].trim() === '') i++;
  }

  return (
    <div className="space-y-3 text-sm text-ink-secondary leading-relaxed">
      {blocks.map((b, idx) => {
        if (b.kind === 'h2') {
          return <h3 key={idx} className="text-sm font-semibold text-ink mt-4">{b.body}</h3>;
        }
        if (b.kind === 'h3') {
          return <h4 key={idx} className="text-sm font-medium text-ink-secondary mt-3">{b.body}</h4>;
        }
        if (b.kind === 'code') {
          return (
            <pre key={idx} className="text-xs bg-surface-raised/60 border border-border-subtle rounded p-3 overflow-x-auto">
              <code>{b.body}</code>
            </pre>
          );
        }
        return <p key={idx} className="whitespace-pre-wrap">{renderInline(b.body)}</p>;
      })}
    </div>
  );
}

function AuditPack({ pack }) {
  if (!pack) return null;
  // Two article paths use the same pack slot. Audit traces have `environment`
  // and `counters`; entity articles set `kind: 'entity_article'` and carry
  // flow_steps + citations.
  if (pack.kind === 'entity_article') {
    return <EntityPack pack={pack} />;
  }
  const env = pack.environment || {};
  const c = pack.counters || {};
  const integ = pack.integrity || {};
  const copyCitation = () => {
    try { navigator.clipboard.writeText(pack.citation_line || ''); } catch (_) {}
  };
  const downloadExport = async () => {
    if (!pack?.export?.available || !pack?.trace_id) return;
    try {
      const { apiService } = await import('../api/apiClient');
      const data = await apiService.exportAuditTrace(pack.trace_id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `neorag-trace-${pack.trace_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {}
  };
  return (
    <aside className="mt-6 pt-5 border-t border-border-subtle space-y-4">
      <div className="text-label tracking-widest text-ink-muted uppercase">
        Evidencia verificable (backend · no Gemini)
      </div>

      <section>
        <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
          Entorno de ejecución
        </div>
        <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
          <Kv k="model" v={env.model} />
          <Kv k="temperature" v={env.temperature} />
          <Kv k="max_output_tokens" v={env.max_output_tokens} />
          <Kv k="app_version" v={env.app_version} />
          <Kv k="prompt_template_version" v={env.prompt_template_version} />
          <Kv k="config_version" v={env.config_version} />
        </dl>
      </section>

      <section>
        <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
          Contadores del trace
        </div>
        <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
          <Kv k="events_total" v={c.events_total} />
          <Kv k="chat_interactions" v={c.chat_interactions} />
          <Kv k="llm_calls" v={c.llm_calls} />
          <Kv k="cypher_executions" v={c.cypher_executions} />
          <Kv k="tokens_in_total" v={c.tokens_in_total} />
          <Kv k="tokens_out_total" v={c.tokens_out_total} />
        </dl>
      </section>

      <section>
        <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
          Integridad criptográfica
        </div>
        <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
          <Kv k="first_chain_hash" v={integ.first_chain_hash} mono />
          <Kv k="last_chain_hash" v={integ.last_chain_hash} mono />
          <Kv k="verified" v={integ.verified === null ? '—' : integ.verified ? '✓ válida' : '✗ ruptura'} />
          {integ.verify_break_at && <Kv k="break_at" v={integ.verify_break_at} mono />}
        </dl>
      </section>

      {Array.isArray(pack.reproduction_notes) && pack.reproduction_notes.length > 0 && (
        <section>
          <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
            Notas de reproducibilidad
          </div>
          <ul className="space-y-1 text-xs text-ink-secondary leading-relaxed list-disc list-inside">
            {pack.reproduction_notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </section>
      )}

      {pack.citation_line && (
        <section>
          <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
            Cita académica
          </div>
          <div className="flex items-start gap-2">
            <code className="flex-1 text-[11px] bg-surface-raised/60 border border-border-subtle rounded p-2 break-all">
              {pack.citation_line}
            </code>
            <button onClick={copyCitation}
                    className="text-label tracking-widest text-ink-muted hover:text-accent-text border border-border-strong rounded px-2 py-1 shrink-0">
              Copiar
            </button>
          </div>
        </section>
      )}

      {pack?.export?.available && (
        <section>
          <button onClick={downloadExport}
                  className="text-label tracking-widest px-4 py-2 border border-accent/40 bg-accent/10 text-accent-text rounded hover:bg-accent/20">
            ↓ Descargar evidencia firmada ({pack.export.format})
          </button>
        </section>
      )}
    </aside>
  );
}

function EntityPack({ pack }) {
  const node = pack.node || {};
  const related = pack.related || {};
  const llm = pack.llm_call || {};
  const stats = pack.article_stats || {};
  const cov = stats.coverage || {};
  const flow = pack.flow_steps || [];
  const citations = pack.citations_detail || [];
  const omitted = pack.omitted_detail || [];

  return (
    <aside className="mt-6 pt-5 border-t border-border-subtle space-y-5">
      <div className="text-label tracking-widest text-ink-muted uppercase">
        Cómo se construyó este artículo (backend · verificable)
      </div>

      {/* Flowchart: 3 steps left-to-right */}
      {flow.length > 0 && (
        <div className="flex flex-col md:flex-row md:items-stretch gap-2 md:gap-0">
          {flow.map((s, i) => (
            <React.Fragment key={s.step}>
              <div className="flex-1 rounded-md border border-border-subtle bg-surface-raised/50 p-3">
                <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
                  {s.step}. {s.title}
                </div>
                <div className="text-sm text-ink-secondary leading-snug">{s.detail}</div>
                <div className="text-xs text-ink-muted mt-1">{s.metric}</div>
              </div>
              {i < flow.length - 1 && (
                <div className="flex md:items-center md:justify-center text-ink-muted shrink-0 px-1">
                  <span className="hidden md:inline">→</span>
                  <span className="md:hidden self-center">↓</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <section>
        <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
          Nodo fuente
        </div>
        <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
          <Kv k="id" v={node.id} mono />
          <Kv k="label" v={node.primary_label} />
          <Kv k="display_name" v={node.display_name} />
          <Kv k="props_total" v={node.props_total} />
          <Kv k="related_total" v={related.total} />
          <Kv k="related_used_in_prompt" v={related.used_in_prompt} />
        </dl>
        {Array.isArray(node.prop_keys) && node.prop_keys.length > 0 && (
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer text-ink-muted">
              Claves JSON disponibles en el nodo ({node.prop_keys.length})
            </summary>
            <div className="mt-1 font-mono text-[11px] bg-surface-raised/60 p-2 rounded break-all">
              {node.prop_keys.join(', ')}
            </div>
          </details>
        )}
        {Array.isArray(related.rel_types) && related.rel_types.length > 0 && (
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer text-ink-muted">
              Tipos de relación encontrados ({related.rel_types.length})
            </summary>
            <div className="mt-1 font-mono text-[11px] bg-surface-raised/60 p-2 rounded break-all">
              {related.rel_types.join(', ')}
            </div>
          </details>
        )}
      </section>

      <section>
        <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
          Llamada al modelo
        </div>
        <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
          <Kv k="model" v={llm.model} />
          <Kv k="temperature" v={llm.temperature} />
          <Kv k="max_output_tokens" v={llm.max_output_tokens} />
          <Kv k="tokens_in" v={llm.tokens_in} />
          <Kv k="tokens_out" v={llm.tokens_out} />
          <Kv k="thoughts_tokens" v={llm.thoughts_tokens} />
          <Kv k="finish_reason" v={llm.finish_reason} />
          <Kv k="duration_ms" v={llm.duration_ms} />
        </dl>
      </section>

      <section>
        <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
          Cobertura del JSON
        </div>
        <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
          <Kv k="props_total (en el nodo)" v={cov.props_total ?? node.props_total} />
          <Kv k="props_used (citadas)" v={cov.props_used} />
          <Kv k="related_total" v={cov.related_total ?? related.total} />
          <Kv k="related_used" v={cov.related_used} />
          <Kv k="citaciones emitidas" v={stats.citations} />
          <Kv k="claves omitidas" v={stats.omitted_keys} />
        </dl>
      </section>

      {citations.length > 0 && (
        <section>
          <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
            Citaciones por oración ({citations.length})
          </div>
          <ul className="space-y-1.5 text-xs">
            {citations.map((c, i) => (
              <li key={i} className="border-l-2 border-border-subtle pl-3">
                <div className="text-ink-secondary">{c.claim}</div>
                <div className="text-ink-muted mt-0.5">
                  keys: <code className="bg-surface-raised/60 px-1 rounded">{Array.isArray(c.keys) ? c.keys.join(', ') : String(c.keys || '')}</code>
                </div>
                {c.values && typeof c.values === 'object' && (
                  <details className="mt-0.5">
                    <summary className="cursor-pointer text-ink-muted">valores citados</summary>
                    <pre className="mt-1 bg-surface-raised/60 p-1.5 rounded text-[11px] whitespace-pre-wrap break-all">
                      {JSON.stringify(c.values, null, 2)}
                    </pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {omitted.length > 0 && (
        <section>
          <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
            Claves omitidas ({omitted.length})
          </div>
          <ul className="space-y-1 text-xs">
            {omitted.map((o, i) => (
              <li key={i} className="text-ink-secondary">
                <code className="bg-surface-raised/60 px-1 rounded">{o.key}</code>
                <span className="text-ink-muted ml-2">→ {o.reason || '—'}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {Array.isArray(pack.reproduction_notes) && pack.reproduction_notes.length > 0 && (
        <section>
          <div className="text-label tracking-widest text-ink-muted uppercase mb-1">
            Notas de reproducibilidad
          </div>
          <ul className="space-y-1 text-xs text-ink-secondary leading-relaxed list-disc list-inside">
            {pack.reproduction_notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </section>
      )}
    </aside>
  );
}

function Kv({ k, v, mono }) {
  const val = v === null || v === undefined || v === '' ? '—' : String(v);
  return (
    <>
      <dt className="text-ink-muted">{k}</dt>
      <dd className={mono ? 'text-ink-secondary font-mono break-all' : 'text-ink-secondary'}>
        {val}
      </dd>
    </>
  );
}

function renderInline(text) {
  // Handles `code` spans and **bold** within paragraphs. Keeps newlines.
  const parts = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) {
      parts.push(<code key={key++} className="text-xs bg-surface-raised/60 px-1 rounded">{tok.slice(1, -1)}</code>);
    } else {
      parts.push(<strong key={key++} className="text-ink">{tok.slice(2, -2)}</strong>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
